import '@apps/web/routes/_app/admin/agent/theme.css';

import { createFileRoute } from '@tanstack/react-router';
import { Bot, Search } from 'lucide-react';
import { Fragment, type ReactNode, useMemo, useState } from 'react';

import { Input } from '@apps/web/components/ui/input';
import {
    SegmentedControl,
    type SegmentedControlOption,
} from '@apps/web/components/ui/segmented_control';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '@apps/web/components/ui/tabs';
import { useAdminAgentCapabilities } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type ApiResponseType } from '@libraries/typescript/api_client';

type CapabilityRegistryResponse = ApiResponseType<
    '/admin/agent/capabilities',
    'get'
>;
type Capability = CapabilityRegistryResponse['capabilities'][number];

type CapabilityTab = 'skills' | 'tools';
type AudienceFilter = 'all' | 'general' | 'admin';

const SKILL_SOURCE_GROUPS: ReadonlyArray<Capability['source_group']> = [
    'admin_workflow_skill',
    'general_agent',
    'automation_run',
    'promotion_probe',
];

const TOOL_SOURCE_GROUPS: ReadonlyArray<Capability['source_group']> = [
    'admin_agent_tool',
    'admin_mcp_tool',
];

const AUDIENCE_FILTER_OPTIONS: ReadonlyArray<
    SegmentedControlOption<AudienceFilter>
> = [
    { value: 'all', label: 'All' },
    { value: 'general', label: 'General' },
    { value: 'admin', label: 'Admin' },
];

const MONO_FONT_FAMILY = 'var(--aa-font-mono, monospace)';

const EMPTY_CAPABILITIES: ReadonlyArray<Capability> = [];

export const Route = createFileRoute('/_app/admin/agent/capabilities')({
    head: () => ({
        meta: [
            {
                title: 'Agent Capabilities | Phoebe Admin',
            },
        ],
    }),
    component: AdminAgentCapabilitiesPage,
});

export function AdminAgentCapabilitiesPage() {
    const { data, isLoading, error } = useAdminAgentCapabilities();
    const [query, setQuery] = useState('');
    const [tab, setTab] = useState<CapabilityTab>('skills');
    const [audience, setAudience] = useState<AudienceFilter>('all');

    const capabilities = data?.capabilities ?? EMPTY_CAPABILITIES;

    const skillCapabilities = useMemo(
        () =>
            capabilities.filter((capability) =>
                SKILL_SOURCE_GROUPS.includes(capability.source_group),
            ),
        [capabilities],
    );
    const toolCapabilities = useMemo(
        () =>
            capabilities.filter((capability) =>
                TOOL_SOURCE_GROUPS.includes(capability.source_group),
            ),
        [capabilities],
    );

    const filteredSkills = useMemo(
        () => filterCapabilities(skillCapabilities, query, audience),
        [skillCapabilities, query, audience],
    );
    const filteredTools = useMemo(
        () => filterCapabilities(toolCapabilities, query, audience),
        [toolCapabilities, query, audience],
    );

    return (
        <div className="admin-agent-page bg-background flex h-[calc(100svh-3.5rem)] max-h-[calc(100svh-3.5rem)] min-h-0 w-full flex-col overflow-hidden p-4 md:h-svh md:max-h-svh">
            <div className="shrink-0 border-b pb-3">
                <div className="flex items-center gap-2">
                    <Bot className="text-foreground h-5 w-5" />
                    <h1 className="text-foreground text-xl font-semibold">
                        Agent Capabilities
                    </h1>
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                    <div className="relative min-w-[260px] flex-[1_1_340px] md:max-w-md">
                        <Search className="text-muted-foreground pointer-events-none absolute top-2.5 left-3 h-4 w-4" />
                        <Input
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            placeholder="Search capabilities"
                            className="h-8 pl-9 text-sm"
                        />
                    </div>
                    <SegmentedControl
                        aria-label="Audience filter"
                        options={AUDIENCE_FILTER_OPTIONS}
                        value={audience}
                        onValueChange={setAudience}
                        layout="auto"
                        size="sm"
                    />
                </div>
            </div>
            <Tabs
                value={tab}
                onValueChange={(value) => setTab(value as CapabilityTab)}
                className="flex min-h-0 flex-1 flex-col gap-3 pt-3"
            >
                <TabsList className="w-fit">
                    <TabsTrigger value="skills">
                        Skills ({skillCapabilities.length})
                    </TabsTrigger>
                    <TabsTrigger value="tools">
                        Tools ({toolCapabilities.length})
                    </TabsTrigger>
                </TabsList>
                <TabsContent
                    value="skills"
                    className="flex min-h-0 flex-1 flex-col gap-2"
                >
                    <CapabilityTablePanel
                        capabilities={filteredSkills}
                        totalCount={skillCapabilities.length}
                        isLoading={isLoading}
                        error={error}
                    />
                </TabsContent>
                <TabsContent
                    value="tools"
                    className="flex min-h-0 flex-1 flex-col gap-2"
                >
                    <CapabilityTablePanel
                        capabilities={filteredTools}
                        totalCount={toolCapabilities.length}
                        isLoading={isLoading}
                        error={error}
                    />
                </TabsContent>
            </Tabs>
        </div>
    );
}

function CapabilityTablePanel({
    capabilities,
    totalCount,
    isLoading,
    error,
}: {
    readonly capabilities: ReadonlyArray<Capability>;
    readonly totalCount: number;
    readonly isLoading: boolean;
    readonly error: unknown;
}) {
    if (isLoading) {
        return (
            <div className="text-muted-foreground flex min-h-0 flex-1 items-center justify-center rounded-md border bg-white text-sm">
                Loading capability registry
            </div>
        );
    }
    if (error) {
        return (
            <div className="flex min-h-0 flex-1 items-center justify-center rounded-md border bg-white text-sm text-red-700">
                Capability registry failed to load
            </div>
        );
    }
    return (
        <Fragment>
            <div className="text-muted-foreground flex shrink-0 items-center justify-between text-sm">
                <span>
                    Showing {capabilities.length} of {totalCount}
                </span>
            </div>
            <div className="min-h-0 flex-1 overflow-hidden rounded-md border bg-white">
                <CapabilityTable capabilities={capabilities} />
            </div>
        </Fragment>
    );
}

function CapabilityTable({
    capabilities,
}: {
    readonly capabilities: ReadonlyArray<Capability>;
}) {
    return (
        <div
            className="h-full min-h-0 overflow-y-auto"
            data-testid="capability-list-pane"
        >
            <Table>
                <TableHeader className="bg-muted/70 sticky top-0 z-10 backdrop-blur">
                    <TableRow>
                        <TableHead className="w-[24%]">Name</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="w-[32%]">Source</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {capabilities.map((capability) => (
                        <TableRow
                            key={capability.capability_id}
                            className="align-top"
                        >
                            <TableCell
                                className="text-foreground font-medium whitespace-normal"
                                style={{ fontFamily: MONO_FONT_FAMILY }}
                            >
                                {capability.name}
                            </TableCell>
                            <TableCell className="text-muted-foreground whitespace-normal">
                                {renderDescriptionWithInlineCode(
                                    capability.description,
                                )}
                            </TableCell>
                            <TableCell
                                className="text-xs break-all"
                                style={{ fontFamily: MONO_FONT_FAMILY }}
                            >
                                {capability.source_file_path ?? '—'}
                            </TableCell>
                        </TableRow>
                    ))}
                    {capabilities.length === 0 && (
                        <TableRow>
                            <TableCell
                                colSpan={3}
                                className="text-muted-foreground h-28 text-center text-sm"
                            >
                                No matching capabilities
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </div>
    );
}

// Render backtick-delimited spans as inline `<code>`. Capability
// descriptions embed identifiers, JSON keys, and API paths as markdown
// code spans; without this they render as raw text with visible
// backticks.
export function renderDescriptionWithInlineCode(text: string): ReactNode {
    const segments = text.split('`');
    if (segments.length < 3) {
        return text;
    }
    return segments.map((segment, index) => {
        if (index % 2 === 0) {
            return <Fragment key={index}>{segment}</Fragment>;
        }
        return (
            <code
                key={index}
                className="bg-muted/50 text-foreground rounded px-1 text-[0.85em]"
                style={{ fontFamily: MONO_FONT_FAMILY }}
            >
                {segment}
            </code>
        );
    });
}

export function filterCapabilities(
    capabilities: ReadonlyArray<Capability>,
    query: string,
    audience: AudienceFilter = 'all',
): ReadonlyArray<Capability> {
    const normalized = query.trim().toLowerCase();
    return capabilities.filter((capability) => {
        if (!capabilityMatchesAudience(capability, audience)) {
            return false;
        }
        if (normalized.length === 0) {
            return true;
        }
        const searchable = [
            capability.name,
            capability.description,
            capability.source_file_path ?? '',
        ]
            .join(' ')
            .toLowerCase();
        return searchable.includes(normalized);
    });
}

function capabilityMatchesAudience(
    capability: Capability,
    audience: AudienceFilter,
): boolean {
    if (audience === 'all') {
        return true;
    }
    if (audience === 'general') {
        return capability.audiences.includes('general_agent');
    }
    return capability.audiences.includes('internal_admin');
}
